import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import {
  Bot, UserRound, Search, Send, Hand, RotateCcw, AlertTriangle, Smartphone, X, CheckCircle2, Plus, Trash2,
  Sparkles, CheckCheck, CircleAlert, Clock, Lock, Save,
} from "lucide-react";
import SegmentedControl from "../../components/SegmentedControl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { getSocket } from "../../lib/socket";
import {
  getLeadConversations, getLeadConversation, interveneLeadConversation, replyLeadConversation, handBackLeadConversation,
  approveLeadConversation, closeLeadConversation, simulateLeadMessage, createLead, getChatbotSettings,
  updateChatbotSettings, getDoctors, getAvailableSlots,
} from "../../api/api";
import {
  LEAD_SOURCES, SOURCE_LABEL, LEAD_STATUS_LABEL, CONVERSATION_STATES, STATE_LABEL, OUTCOMES, OUTCOME_LABEL, LEAD_CHAT_EVENT,
} from "../../lib/lead-constants";

// Every lead's first conversation is with the AI assistant. Staff watch,
// can intervene at any moment, and approve (or intervene on) everything the
// assistant concludes — nothing it proposes takes effect until then.

const STATE_STYLE = {
  pending_approval: "bg-amber-500/15 text-amber-700",
  needs_human: "bg-destructive/10 text-destructive",
  bot_active: "bg-green-600/10 text-green-700",
  human_active: "bg-blue-600/10 text-blue-700",
  closed: "bg-muted text-muted-foreground",
};

const initials = (name, phone) =>
  (name || "").replace(/[^A-Za-z ]/g, "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() ||
  String(phone || "?").slice(-2);

const errMsg = (err, fallback) => err?.message || fallback;

function StateBadge({ conversation }) {
  const urgent = conversation.state === "needs_human" && conversation.handover?.priority === "urgent";
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase", urgent ? "bg-destructive text-destructive-foreground" : STATE_STYLE[conversation.state])}>
      {urgent && <AlertTriangle className="h-3 w-3" />}
      {conversation.state === "bot_active" && <Bot className="h-3 w-3" />}
      {conversation.state === "human_active" && <UserRound className="h-3 w-3" />}
      {conversation.state === "pending_approval" && <Clock className="h-3 w-3" />}
      {conversation.state === "human_active" && conversation.owner_name ? conversation.owner_name : STATE_LABEL[conversation.state]}
    </span>
  );
}

// ------------------------------------------------------------- message bubble

function Bubble({ m, phone }) {
  if (m.sender === "system") {
    if (phone) return null;
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full bg-white/70 px-3 py-1 text-center text-[11px] text-[#54656f]">
          {m.text} · {moment(m.createdAt).format("h:mm A")}
        </span>
      </div>
    );
  }
  const fromPatient = m.sender === "patient";
  const mine = phone ? fromPatient : !fromPatient;
  return (
    <div className={cn("my-1.5 flex", mine ? "justify-end" : "justify-start")}>
      <div className="max-w-[82%] space-y-0.5">
        {!phone && !fromPatient && (
          <p className="flex items-center justify-end gap-1 text-[10px] font-medium text-[#54656f]">
            {m.sender === "bot" ? <><Bot className="h-3 w-3" /> {m.sender_name || "Assistant"}</> : <><UserRound className="h-3 w-3" /> {m.sender_name}</>}
          </p>
        )}
        <div
          className={cn(
            "whitespace-pre-line rounded-lg px-3 py-2 text-sm text-[#111b21] shadow-sm",
            mine ? "rounded-tr-none bg-[#d9fdd3]" : "rounded-tl-none bg-white",
            !phone && m.sender === "staff" && "bg-[#dbeafe]"
          )}
        >
          {m.text}
          <span className="ml-2 inline-flex items-center gap-0.5 align-bottom text-[10px] text-[#667781]">
            {moment(m.createdAt).format("h:mm A")}
            {!phone && !fromPatient && m.status === "failed" && <CircleAlert className="h-3 w-3 text-destructive" />}
            {!phone && !fromPatient && ["delivered", "read"].includes(m.status) && (
              <CheckCheck className={cn("h-3 w-3", m.status === "read" ? "text-[#53bdeb]" : "")} />
            )}
          </span>
        </div>
        {!phone && m.status === "failed" && (
          <p className="text-right text-[10px] text-destructive">Not delivered: {m.error}</p>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------- simulator phone

function PhoneSimulator({ detail, onClose, clinicName }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const { conversation, messages } = detail;
  const botTyping = conversation.state === "bot_active" && messages.filter((m) => m.sender !== "system").at(-1)?.sender === "patient";
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, botTyping]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await simulateLeadMessage(conversation._id, text.trim());
      setText("");
    } catch (err) {
      toast.error(errMsg(err, "Could not send."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex h-[600px] max-h-[calc(100vh-2rem)] w-[min(340px,calc(100vw-2rem))] flex-col overflow-hidden rounded-[2rem] border-[6px] border-[#111b21] bg-[#efeae2] shadow-2xl">
      <div className="flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-xs font-bold">{initials(clinicName)}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{clinicName}</p>
          <p className="text-[11px] opacity-80">{botTyping ? "typing…" : "Business account"}</p>
        </div>
        <button onClick={onClose} aria-label="Close simulator" className="rounded p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-2.5 py-2">
        <p className="mx-auto my-2 w-fit rounded-md bg-[#fff5c4] px-2 py-1 text-center text-[10px] text-[#54656f]">
          Test chat — you are the patient. Nothing is sent to a real phone.
        </p>
        {messages.map((m) => <Bubble key={m._id} m={m} phone />)}
        {botTyping && <p className="ml-1 w-fit rounded-lg bg-white px-3 py-2 text-xs text-[#667781] shadow-sm">typing…</p>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-1.5 bg-[#f0f2f5] p-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message" className="h-9 min-w-0 flex-1 rounded-full bg-white px-3 text-sm outline-none" />
        <button type="submit" disabled={sending} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#00a884] text-white disabled:opacity-60" aria-label="Send">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}

// ------------------------------------------------------------ outcome editor

const field = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

function OutcomeEditor({ value, onChange, doctors }) {
  const [slots, setSlots] = useState({ loading: false, list: [], note: "" });
  const set = (patch) => onChange({ ...value, ...patch });
  const isBooking = value.type === "book_appointment";

  useEffect(() => {
    if (!isBooking || !value.doctor_id || !value.iso_date) {
      setSlots({ loading: false, list: [], note: "" });
      return;
    }
    let live = true;
    setSlots((s) => ({ ...s, loading: true }));
    getAvailableSlots({ doctor_id: value.doctor_id, iso_date: value.iso_date })
      .then((res) => {
        if (!live) return;
        const d = res.data;
        setSlots({
          loading: false,
          list: (d.slots || []).filter((s) => !s.taken).map((s) => s.time),
          note: d.closed ? "Clinic closed that day" : d.onLeave ? "Doctor on leave that day" : "",
        });
      })
      .catch(() => live && setSlots({ loading: false, list: [], note: "Could not load slots" }));
    return () => { live = false; };
  }, [isBooking, value.doctor_id, value.iso_date]);

  const slotOptions = value.slot_time && !slots.list.includes(value.slot_time) ? [value.slot_time, ...slots.list] : slots.list;

  return (
    <div className="space-y-2">
      <select value={value.type} onChange={(e) => set({ type: e.target.value })} className={field} aria-label="Outcome">
        {OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {isBooking && (
        <>
          <Input value={value.patient_name || ""} onChange={(e) => set({ patient_name: e.target.value })} placeholder="Patient name" className="h-9" />
          <select value={value.doctor_id || ""} onChange={(e) => set({ doctor_id: e.target.value, slot_time: "" })} className={field} aria-label="Doctor">
            <option value="">Choose doctor</option>
            {doctors.map((d) => <option key={d._id} value={d._id}>{d.name} · ₹{d.fee}</option>)}
          </select>
          <Input type="date" value={value.iso_date || ""} min={moment().format("YYYY-MM-DD")} onChange={(e) => set({ iso_date: e.target.value, slot_time: "" })} className="h-9" />
          <select value={value.slot_time || ""} onChange={(e) => set({ slot_time: e.target.value })} className={field} aria-label="Slot">
            <option value="">{slots.loading ? "Loading slots…" : slots.note || "Choose a free slot"}</option>
            {slotOptions.map((t) => (
              <option key={t} value={t}>{t}{!slots.loading && !slots.list.includes(t) ? " (no longer free)" : ""}</option>
            ))}
          </select>
        </>
      )}
      {value.type === "follow_up" && (
        <Input type="date" value={value.follow_up_on || ""} min={moment().format("YYYY-MM-DD")} onChange={(e) => set({ follow_up_on: e.target.value })} className="h-9" aria-label="Follow up on" />
      )}
      <Input value={value.note || ""} onChange={(e) => set({ note: e.target.value })} placeholder="Note (optional)" className="h-9" />
    </div>
  );
}

const cleanOutcome = (o) => {
  const out = { type: o.type, note: o.note || "" };
  if (o.type === "book_appointment") Object.assign(out, { doctor_id: o.doctor_id, iso_date: o.iso_date, slot_time: o.slot_time, patient_name: o.patient_name || "" });
  if (o.type === "follow_up") out.follow_up_on = o.follow_up_on || null;
  return out;
};

const describeOutcome = (o) => {
  if (!o) return "—";
  if (o.type === "book_appointment") {
    return `${o.patient_name || "Patient"} with ${o.doctor_name || "doctor"} · ${moment(o.iso_date).format("ddd D MMM")} at ${o.slot_time}`;
  }
  if (o.type === "follow_up") return o.follow_up_on ? `Follow up on ${moment(o.follow_up_on).format("ddd D MMM")}` : "Follow up later";
  return OUTCOME_LABEL[o.type];
};

// ------------------------------------------------------------ approval card

function ApprovalCard({ detail, doctors, onDone, onIntervene }) {
  const proposed = detail.conversation.proposed_outcome;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...proposed, doctor_id: proposed?.doctor_id || "" }));
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await approveLeadConversation(detail.conversation._id, editing ? { outcome: cleanOutcome(draft) } : {});
      toast.success(proposed.type === "book_appointment" ? "Approved — appointment booked and patient informed." : "Approved.");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Could not approve."));
      if (proposed.type === "book_appointment") setEditing(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Clock className="h-4 w-4" /> The assistant concluded this chat — your approval is needed</p>
          <p className="mt-1 text-sm"><span className="font-medium">{OUTCOME_LABEL[proposed.type]}:</span> {describeOutcome(proposed)}</p>
          {detail.conversation.summary && <p className="mt-0.5 text-xs text-muted-foreground">{detail.conversation.summary}</p>}
          <p className="mt-0.5 text-[11px] text-muted-foreground">Waiting {moment(detail.conversation.concluded_at).fromNow(true)}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {!editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>}
          <Button size="sm" variant="outline" onClick={onIntervene} disabled={busy}><Hand className="h-3.5 w-3.5" /> Intervene</Button>
          <Button size="sm" onClick={approve} disabled={busy}><CheckCircle2 className="h-3.5 w-3.5" /> Approve</Button>
        </div>
      </div>
      {editing && (
        <div className="mt-3 max-w-md rounded-md border border-border bg-card p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Approve with changes</p>
          <OutcomeEditor value={draft} onChange={setDraft} doctors={doctors} />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ dialogs

function CloseDialog({ open, onOpenChange, conversationId, doctors, onDone }) {
  const [draft, setDraft] = useState({ type: "info_only" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setDraft({ type: "info_only" }); }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await closeLeadConversation(conversationId, { outcome: cleanOutcome(draft) });
      toast.success("Chat closed.");
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Could not close the chat."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Close chat</DialogTitle>
          <DialogDescription>You handled this one, so it closes straight away — no approval step.</DialogDescription>
        </DialogHeader>
        <OutcomeEditor value={draft} onChange={setDraft} doctors={doctors} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>Close chat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_LEAD = { name: "", phone: "", source: "website", campaign: "", service_interest: "", note: "", test: false };

function AddLeadDialog({ open, onOpenChange, onCreated, testMode }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ ...EMPTY_LEAD, test: testMode }); }, [open, testMode]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await createLead(form);
      toast.success(res.message || "Lead added.");
      onOpenChange(false);
      onCreated(res.data.conversation_id, form.test);
    } catch (err) {
      toast.error(errMsg(err, "Could not add the lead."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit} className="space-y-3">
          <DialogHeader>
            <DialogTitle>{testMode ? "Test the assistant" : "Add lead"}</DialogTitle>
            <DialogDescription>
              {testMode
                ? "Creates a test lead and opens a phone where you play the patient. Nothing reaches a real number. Without an AI key, a free scripted demo assistant answers — try \"skin doctor tomorrow\", fees, or \"chest pain\"."
                : "The assistant reaches out on WhatsApp. If WhatsApp isn't connected, the lead comes to the desk to call."}
            </DialogDescription>
          </DialogHeader>
          <Input value={form.name} onChange={set("name")} placeholder="Name" />
          <Input value={form.phone} onChange={set("phone")} placeholder="Mobile number" required inputMode="tel" />
          <select value={form.source} onChange={set("source")} className={field} aria-label="Source">
            {LEAD_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <Input value={form.campaign} onChange={set("campaign")} placeholder="Campaign / ad (optional)" />
          <Input value={form.service_interest} onChange={set("service_interest")} placeholder="Interested in (optional)" />
          <textarea value={form.note} onChange={set("note")} rows={2} placeholder="Note for the assistant (optional)" className="w-full rounded-md border border-input bg-background p-2 text-sm" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{testMode ? "Start test chat" : "Add lead"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// What's answering patients right now, in words the desk understands.
const aiStatusLabel = (d) => {
  if (d.ai_provider === "ollama") {
    if (!d.ai_reachable) return `Own-server AI (Ollama) not running — start Ollama; chats go to staff meanwhile`;
    if (!d.ai_configured) return `Ollama is running but model ${d.ai_model} isn't downloaded — run: ollama pull ${d.ai_model}`;
    return `Own-server AI connected · ${d.ai_model} (free, private)`;
  }
  if (d.ai_provider === "anthropic") return d.ai_configured ? "Claude AI connected" : "Claude selected but ANTHROPIC_API_KEY is missing";
  return "Free demo mode — test chats use a scripted assistant; no AI configured";
};

// ------------------------------------------------------------ knowledge base

function AssistantSettings() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ topic: "", answer: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getChatbotSettings().then((res) => setData(res.data)).catch((err) => toast.error(errMsg(err, "Could not load assistant settings.")));
  }, []);

  if (!data) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;

  const patch = (p) => { setData((d) => ({ ...d, ...p })); setDirty(true); };
  const updateKb = (i, p) => patch({ knowledge_base: data.knowledge_base.map((k, j) => (j === i ? { ...k, ...p } : k)) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateChatbotSettings({
        enabled: data.enabled,
        assistant_name: data.assistant_name,
        approval_sla_minutes: Number(data.approval_sla_minutes),
        silent_fallback_minutes: Number(data.silent_fallback_minutes),
        knowledge_base: data.knowledge_base.map(({ topic, answer }) => ({ topic, answer })),
      });
      setData((d) => ({ ...d, ...res.data }));
      setDirty(false);
      toast.success("Saved — the assistant uses this from its next reply.");
    } catch (err) {
      toast.error(errMsg(err, "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    if (!draft.topic.trim() || !draft.answer.trim()) return toast.error("Topic and answer are required.");
    patch({ knowledge_base: [...data.knowledge_base, { topic: draft.topic.trim(), answer: draft.answer.trim() }] });
    setDraft({ topic: "", answer: "" });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={data.enabled} onChange={(e) => patch({ enabled: e.target.checked })} className="h-4 w-4" />
              Assistant answers new leads
            </label>
            <label className="text-sm">
              <span className="text-xs text-muted-foreground">Assistant name</span>
              <Input value={data.assistant_name} onChange={(e) => patch({ assistant_name: e.target.value })} className="mt-1 h-9" />
            </label>
            <label className="text-sm">
              <span className="text-xs text-muted-foreground">Remind if unapproved after (min)</span>
              <Input type="number" min={5} max={1440} value={data.approval_sla_minutes} onChange={(e) => patch({ approval_sla_minutes: e.target.value })} className="mt-1 h-9" />
            </label>
            <label className="text-sm">
              <span className="text-xs text-muted-foreground">If bot didn't understand and no one replies, send "we'll get back to you" after (min)</span>
              <Input type="number" min={1} max={1440} value={data.silent_fallback_minutes} onChange={(e) => patch({ silent_fallback_minutes: e.target.value })} className="mt-1 h-9" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Badge variant={data.ai_configured ? "outline" : "warning"}>{aiStatusLabel(data)}</Badge>
            <Badge variant={data.whatsapp_configured ? "outline" : "warning"}>{data.whatsapp_configured ? "WhatsApp connected" : "WhatsApp not connected — real leads go to staff"}</Badge>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Answers the assistant may quote — fees, timings, offers, insurance, location. Doctors and free slots are read live from MediPro, so they don't belong here.
        </p>
        {data.knowledge_base.map((k, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <input value={k.topic} onChange={(e) => updateKb(i, { topic: e.target.value })} className="w-full bg-transparent font-medium outline-none" aria-label="Topic" />
              <button onClick={() => patch({ knowledge_base: data.knowledge_base.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive" aria-label={`Delete ${k.topic}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <textarea value={k.answer} onChange={(e) => updateKb(i, { answer: e.target.value })} rows={2} className="mt-2 w-full resize-y rounded-md border border-input bg-background p-2 text-sm" aria-label="Answer" />
          </div>
        ))}
        {data.knowledge_base.length === 0 && <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Nothing yet. Add the questions patients ask most.</p>}
      </div>
      <div className="h-fit space-y-3 lg:sticky lg:top-0">
        <form onSubmit={add} className="space-y-3 rounded-lg border border-border bg-card p-4">
          <p className="flex items-center gap-2 font-medium"><Plus className="h-4 w-4" /> Teach the assistant</p>
          <Input value={draft.topic} onChange={(e) => setDraft((d) => ({ ...d, topic: e.target.value }))} placeholder="Topic, e.g. Consultation fees" />
          <textarea value={draft.answer} onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))} rows={4} placeholder="The answer, as you'd tell a patient" className="w-full rounded-md border border-input bg-background p-2 text-sm" />
          <Button type="submit" variant="outline" className="w-full">Add</Button>
        </form>
        <Button className="w-full" onClick={save} disabled={!dirty || saving}><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- page

const FILTERS = [{ key: "all", label: "All" }, ...CONVERSATION_STATES];

export default function LeadInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("c");
  const [view, setView] = useState("inbox");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [list, setList] = useState({ data: [], counts: {} });
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [simOpen, setSimOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [addMode, setAddMode] = useState(null); // null | "lead" | "test"
  const clinicName = useSelector((s) => s.settings.data?.clinic?.name) || "Clinic";
  const endRef = useRef(null);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  const select = (id) => setSearchParams(id ? { c: id } : {}, { replace: true });

  const loadList = useCallback(() => {
    const params = { search: search || undefined, state: filter === "all" ? undefined : filter };
    return getLeadConversations(params)
      .then((res) => setList({ data: res.data, counts: res.counts || {} }))
      .catch((err) => toast.error(errMsg(err, "Could not load conversations.")));
  }, [filter, search]);

  const loadDetail = useCallback((id = selectedRef.current) => {
    if (!id) return setDetail(null);
    return getLeadConversation(id)
      .then((res) => { if (selectedRef.current === id) setDetail(res.data); })
      .catch((err) => toast.error(errMsg(err, "Could not load the conversation.")));
  }, []);

  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { setDetail(null); setReply(""); loadDetail(selectedId); }, [selectedId, loadDetail]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [detail?.messages.length, selectedId]);

  useEffect(() => {
    getDoctors({ limit: 200 }).then((res) => setDoctors(res.data || [])).catch(() => {});
  }, []);

  // Live: every change to any lead chat refreshes the list, and the open
  // thread when it's the one that changed. A slow poll covers dropped events.
  useEffect(() => {
    const socket = getSocket();
    const onChat = ({ conversation_id }) => {
      loadList();
      if (conversation_id === selectedRef.current) loadDetail(conversation_id);
    };
    socket?.on(LEAD_CHAT_EVENT, onChat);
    const poll = setInterval(() => { loadList(); loadDetail(); }, 30000);
    return () => { socket?.off(LEAD_CHAT_EVENT, onChat); clearInterval(poll); };
  }, [loadList, loadDetail]);

  const act = async (fn, success) => {
    try {
      await fn();
      if (success) toast.success(success);
      await Promise.all([loadDetail(), loadList()]);
    } catch (err) {
      toast.error(errMsg(err, "Something went wrong."));
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !detail) return;
    const text = reply.trim();
    setReply("");
    await act(() => replyLeadConversation(detail.conversation._id, text));
  };

  const conversation = detail?.conversation;
  const lead = detail?.lead;
  const isSimulator = conversation?.channel === "simulator";
  const awaiting = list.counts.pending_approval || 0;
  const needsHuman = list.counts.needs_human || 0;
  const total = useMemo(() => Object.values(list.counts).reduce((a, b) => a + b, 0), [list.counts]);

  return (
    <div className={cn("space-y-4", simOpen && isSimulator && "xl:pr-[356px]")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Lead Inbox</h2>
          <p className="text-sm text-muted-foreground">
            The assistant talks to every new lead first. Intervene any time; approve what it concludes.
            {awaiting > 0 && <span className="ml-1 font-medium text-amber-700">{awaiting} awaiting approval.</span>}
            {needsHuman > 0 && <span className="ml-1 font-medium text-destructive">{needsHuman} need a person.</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SegmentedControl
            items={[{ value: "inbox", label: "Inbox", count: awaiting + needsHuman || undefined }, { value: "assistant", label: "Assistant" }]}
            value={view}
            onChange={setView}
          />
          <Button variant="outline" onClick={() => setAddMode("test")}><Smartphone className="h-4 w-4" /> Test as patient</Button>
          <Button onClick={() => setAddMode("lead")}><Plus className="h-4 w-4" /> Add lead</Button>
        </div>
      </div>

      {view === "assistant" ? (
        <AssistantSettings />
      ) : (
        <div className="grid h-[calc(100vh-14rem)] min-h-[560px] overflow-hidden rounded-lg border border-border bg-card lg:grid-cols-[320px_1fr_280px]">
          {/* conversation list */}
          <div className={cn("flex min-h-0 flex-col border-r border-border", selectedId && "hidden lg:flex")}>
            <div className="space-y-2 border-b border-border p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone" className="h-9 pl-9" />
              </div>
              <div className="flex flex-wrap gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs",
                      filter === f.key ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
                      filter !== f.key && f.key === "pending_approval" && awaiting > 0 && "border-amber-500/50 text-amber-700",
                      filter !== f.key && f.key === "needs_human" && needsHuman > 0 && "border-destructive/40 text-destructive"
                    )}
                  >
                    {f.label} <span className="tabular-nums opacity-70">{f.key === "all" ? total : list.counts[f.key] || 0}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {list.data.map((c) => {
                const l = c.lead_id || {};
                return (
                  <button
                    key={c._id}
                    onClick={() => select(c._id)}
                    className={cn("flex w-full gap-3 border-b border-border px-3 py-3 text-left hover:bg-muted/50", selectedId === c._id && "bg-muted")}
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">{initials(l.name, l.phone)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{l.name || `+91 ${l.phone}`}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{moment(c.last_message_at).fromNow(true)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {c.last_message?.sender === "bot" ? "🤖 " : c.last_message?.sender === "staff" ? "👤 " : ""}
                        {c.last_message?.text?.split("\n")[0] || "—"}
                      </span>
                      <span className="mt-1 flex items-center gap-1.5">
                        <StateBadge conversation={c} />
                        <span className="truncate text-[10px] text-muted-foreground">{SOURCE_LABEL[l.source]}{c.channel === "simulator" ? " · test" : ""}</span>
                        {c.unread > 0 && <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#25d366] px-1 text-[10px] font-semibold text-white">{c.unread}</span>}
                      </span>
                    </span>
                  </button>
                );
              })}
              {list.data.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No conversations here.</p>}
            </div>
          </div>

          {/* thread */}
          {detail ? (
            <div className="flex min-h-0 flex-col">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <button onClick={() => select(null)} className="rounded p-1 hover:bg-muted lg:hidden" aria-label="Back to list"><X className="h-4 w-4" /></button>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{lead.name || "Unknown"} <StateBadge conversation={conversation} /></p>
                    <p className="text-xs text-muted-foreground">+91 {lead.phone} · {SOURCE_LABEL[lead.source]}{isSimulator ? " · test chat" : ""}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isSimulator && <Button size="sm" variant="outline" onClick={() => setSimOpen(true)}><Smartphone className="h-3.5 w-3.5" /> Patient phone</Button>}
                  {["bot_active", "needs_human"].includes(conversation.state) && (
                    <Button size="sm" variant={conversation.state === "needs_human" ? "default" : "outline"} onClick={() => act(() => interveneLeadConversation(conversation._id), "You've taken over — the assistant is paused.")}>
                      <Hand className="h-3.5 w-3.5" /> {conversation.state === "needs_human" ? "Take over" : "Intervene"}
                    </Button>
                  )}
                  {["human_active", "needs_human"].includes(conversation.state) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => act(() => handBackLeadConversation(conversation._id), "Handed back to the assistant.")}>
                        <RotateCcw className="h-3.5 w-3.5" /> Hand back to bot
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCloseOpen(true)}><Lock className="h-3.5 w-3.5" /> Close</Button>
                    </>
                  )}
                </div>
              </div>

              {conversation.state === "pending_approval" && conversation.proposed_outcome && (
                <ApprovalCard
                  key={conversation.concluded_at}
                  detail={detail}
                  doctors={doctors}
                  onDone={() => Promise.all([loadDetail(), loadList()])}
                  onIntervene={() => act(() => interveneLeadConversation(conversation._id), "You've taken over this chat.")}
                />
              )}
              {conversation.state === "needs_human" && (
                <div className={cn("flex items-center gap-2 px-4 py-2 text-sm", conversation.handover?.priority === "urgent" ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning")}>
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <b>{conversation.handover?.reason}</b> — waiting {moment(conversation.handover?.at).fromNow(true)}.{" "}
                    {conversation.handover?.category !== "not_understood"
                      ? "The assistant has stopped replying."
                      : conversation.handover?.fallback_sent_at
                        ? "No one replied in time, so the patient got an automatic \"we'll get back to you\" — please reply."
                        : "The assistant sent nothing — the patient is waiting for your reply."}
                  </span>
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto bg-[#efeae2] px-4 py-3">
                {detail.messages.map((m) => <Bubble key={m._id} m={m} />)}
                <div ref={endRef} />
              </div>
              <form onSubmit={sendReply} className="flex items-center gap-2 border-t border-border p-3">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={conversation.state === "human_active" ? "Reply to the patient" : "Type to reply — this takes the chat over from the assistant"}
                  className="h-10"
                />
                <Button type="submit" disabled={!reply.trim()} aria-label="Send"><Send className="h-4 w-4" /></Button>
              </form>
            </div>
          ) : (
            <div className={cn("items-center justify-center p-6 text-center text-sm text-muted-foreground", selectedId ? "flex" : "hidden lg:flex")}>
              {selectedId ? "Loading…" : "Select a conversation"}
            </div>
          )}

          {/* details */}
          {detail && (
            <div className="hidden min-h-0 space-y-4 overflow-y-auto border-l border-border p-4 lg:block">
              {conversation.summary && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assistant summary</p>
                  <p className="mt-1 flex gap-1.5 text-sm"><Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {conversation.summary}</p>
                </div>
              )}
              {conversation.bot_error && (
                <div className="rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                  <p className="font-medium">Assistant error</p>
                  <p className="mt-0.5 break-words">{conversation.bot_error}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lead</p>
                <dl className="mt-1 space-y-1 text-sm">
                  <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Status</dt><dd>{LEAD_STATUS_LABEL[lead.status]}</dd></div>
                  <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Source</dt><dd>{SOURCE_LABEL[lead.source]}</dd></div>
                  {lead.campaign && <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Campaign</dt><dd className="text-right">{lead.campaign}</dd></div>}
                  {lead.service_interest && <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Interest</dt><dd className="text-right">{lead.service_interest}</dd></div>}
                  {lead.follow_up_on && <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Follow up</dt><dd>{moment(lead.follow_up_on).format("D MMM")}</dd></div>}
                  <div className="flex justify-between gap-2"><dt className="text-muted-foreground">Since</dt><dd>{moment(lead.createdAt).format("D MMM, h:mm A")}</dd></div>
                </dl>
                {lead.note && <p className="mt-2 whitespace-pre-line rounded-md bg-muted/50 p-2 text-xs">{lead.note}</p>}
              </div>
              {conversation.approval?.decision || conversation.approval?.applied_outcome ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Decision</p>
                  <p className="mt-1 text-sm">
                    {conversation.approval.decision === "approved" ? "Approved" : conversation.approval.decision === "intervened" ? "Intervened" : "Closed"} by {conversation.approval.by_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{moment(conversation.approval.at).format("D MMM, h:mm A")}</p>
                  {conversation.approval.applied_outcome && <p className="mt-1 text-sm">{describeOutcome(conversation.approval.applied_outcome)}</p>}
                </div>
              ) : null}
              {conversation.proposed_outcome && conversation.state !== "pending_approval" && conversation.approval?.decision === "intervened" && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assistant had proposed</p>
                  <p className="mt-1 text-sm">{describeOutcome(conversation.proposed_outcome)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {simOpen && isSimulator && detail && <PhoneSimulator detail={detail} clinicName={clinicName} onClose={() => setSimOpen(false)} />}
      <CloseDialog open={closeOpen} onOpenChange={setCloseOpen} conversationId={conversation?._id} doctors={doctors} onDone={() => Promise.all([loadDetail(), loadList()])} />
      <AddLeadDialog
        open={!!addMode}
        testMode={addMode === "test"}
        onOpenChange={(open) => !open && setAddMode(null)}
        onCreated={(id, test) => { select(id); loadList(); if (test) setSimOpen(true); }}
      />
    </div>
  );
}
